/*
 * Slide Templates — fetch lifecycle + selection state for the panel.
 *
 * The same panel is mounted in two places with different capabilities, so the
 * hook is parameterised over both ends of its lifecycle:
 *
 *   source          where the manifest comes from — `{kind:"api"}` fetches it
 *                   here (the task pane, which can authorise the request), or
 *                   `{kind:"external"}` mirrors a load state someone else
 *                   owns. The Office Dialog window uses the latter: it has no
 *                   bearer token, so the pane fetches on its behalf and the
 *                   window's own channel hook supplies the resulting state
 *                   (see asset-library/hooks/useDialogSlideTemplates.ts).
 *   commitSelection what "Insert into deck" does — by default it inserts via
 *                   PowerPoint.run(); the dialog overrides it to post the
 *                   selected ids to the pane and await its reply, because
 *                   PowerPoint.run() is never available inside a dialog.
 *
 * Selection state is always local either way — only loading and committing
 * need to leave the window.
 *
 * Mirrors usePhotoHeadshot.ts's mount/abort discipline: an AbortController
 * tied to the effect's cleanup so an unmount mid-fetch doesn't try to set
 * state on a gone component, and a `isMountedRef` guard around every
 * `setState` call for the same reason.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { fetchSlideTemplates, logSlideTemplatesError, type SlideTemplate } from "../services/slideTemplatesApi";
import { insertSlideTemplates } from "../services/slideTemplateInsert";
import type { ExtractedSlideImage } from "../../shared/services/pptxSlideImageExtraction";
import { getErrorMessage, isAbortError } from "../../PhotoHeadshot/services/photoHeadApi";

const FETCH_FAILED_MESSAGE = "Couldn't load slide templates";
const INSERT_FAILED_MESSAGE = "Inserting the selected template(s) failed.";
const NO_SLIDE_DATA_MESSAGE = "This template has no insertable slide content yet.";

export type SlideTemplatesLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; templates: readonly SlideTemplate[] };

export type SlideTemplatesSource =
  /** Fetch here. Only valid where the request can be authorised — the pane. */
  | { readonly kind: "api" }
  /** Mirror a load state owned elsewhere, and delegate Retry to its owner. */
  | { readonly kind: "external"; readonly state: SlideTemplatesLoadState; readonly onRetry: () => void };

export interface UseSlideTemplatesOptions {
  /** Defaults to `{kind:"api"}` — the in-pane path. */
  readonly source?: SlideTemplatesSource;
  /**
   * Overrides how a selection is committed, resolving with the number of
   * slides that landed in the deck. Defaults to inserting them here. Pass a
   * stable (memoised) callback — an unstable one only re-creates
   * `insertSelected`, but that churns the panel's imperative handle for no
   * reason.
   */
  readonly commitSelection?: (selectedIds: readonly string[]) => Promise<number>;
  /**
   * Overrides how a card's real photo(s) are loaded — see
   * hooks/useSlideTemplateThumbnail.ts's "ONE CALLER PATH, TWO HOSTS". Both
   * the in-pane and Office Dialog paths now fetch `template.sourceFileUrl`
   * directly (a SAS query string needs no bearer token), so neither caller
   * passes an override today; this stays available for a future host that
   * genuinely can't fetch for itself. Read directly off this options object
   * by SlideTemplatesPanel — it isn't part of this hook's own return value.
   */
  readonly loadThumbnail?: (template: SlideTemplate) => Promise<readonly ExtractedSlideImage[]>;
}

const API_SOURCE: SlideTemplatesSource = { kind: "api" };

export function useSlideTemplates({ source = API_SOURCE, commitSelection }: UseSlideTemplatesOptions = {}) {
  const http = useDeckorateHttp();
  const [fetchedState, setFetchedState] = useState<SlideTemplatesLoadState>({ status: "loading" });
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [insertError, setInsertError] = useState<string | null>(null);
  /** Confirmation of the last successful insert — the dialog window is the
   *  only UI now, so it has to report the outcome itself. */
  const [insertStatus, setInsertStatus] = useState<string | null>(null);
  const [isInserting, setIsInserting] = useState(false);

  const isMountedRef = useRef(true);
  const isExternal = source.kind === "external";

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setFetchedState({ status: "loading" });
      try {
        const templates = await fetchSlideTemplates(http, signal ? { signal } : {});
        if (!isMountedRef.current) return;
        setFetchedState({ status: "ready", templates });
      } catch (error) {
        if (isAbortError(error)) return;
        const message = getErrorMessage(error, FETCH_FAILED_MESSAGE);
        logSlideTemplatesError("slide-templates-fetch", message);
        if (!isMountedRef.current) return;
        setFetchedState({ status: "error", message });
      }
    },
    [http],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // An external source owns its own loading; fetching here would duplicate a
    // request this context can't even authorise.
    if (isExternal) return;
    const controller = new AbortController();
    load(controller.signal).catch((error: unknown) =>
      logSlideTemplatesError("slide-templates-fetch-unhandled", getErrorMessage(error, FETCH_FAILED_MESSAGE)),
    );
    return () => controller.abort();
  }, [isExternal, load]);

  const externalRetry = source.kind === "external" ? source.onRetry : null;
  const retry = useCallback(() => {
    if (externalRetry) {
      externalRetry();
      return;
    }
    load().catch((error: unknown) => logSlideTemplatesError("slide-templates-retry", getErrorMessage(error, FETCH_FAILED_MESSAGE)));
  }, [externalRetry, load]);

  const loadState = source.kind === "external" ? source.state : fetchedState;

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const templates = loadState.status === "ready" ? loadState.templates : [];

  // Only templates still present in the current manifest count as selected —
  // a retry that returns a different set must not leave phantom ids selected.
  const selectedTemplates = useMemo(
    () => templates.filter((template) => selectedIds.has(template.id)),
    [templates, selectedIds],
  );
  const insertableSelected = useMemo(
    () => selectedTemplates.filter((template) => template.insertable),
    [selectedTemplates],
  );

  const insertSelected = useCallback(async () => {
    if (isInserting || selectedTemplates.length === 0) return;
    if (insertableSelected.length === 0) {
      setInsertError(NO_SLIDE_DATA_MESSAGE);
      return;
    }

    setIsInserting(true);
    setInsertError(null);
    setInsertStatus(null);
    try {
      const ids = insertableSelected.map((template) => template.id);
      const inserted = commitSelection ? await commitSelection(ids) : await insertSlideTemplates(templates, ids);
      if (isMountedRef.current) {
        setSelectedIds(new Set());
        const slideCountLabel = inserted === 1 ? "1 slide" : `${inserted} slides`;
        setInsertStatus(`Inserted ${slideCountLabel}.`);
      }
    } catch (error) {
      const message = getErrorMessage(error, INSERT_FAILED_MESSAGE);
      logSlideTemplatesError("slide-templates-insert", message);
      if (isMountedRef.current) setInsertError(message);
    } finally {
      if (isMountedRef.current) setIsInserting(false);
    }
  }, [isInserting, selectedTemplates, insertableSelected, templates, commitSelection]);

  return {
    loadState,
    insertStatus,
    templates,
    selectedIds,
    selectedCount: selectedTemplates.length,
    toggleSelected,
    retry,
    insertSelected,
    isInserting,
    insertError,
  };
}
